import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

const initialState = {
  excludedChips: {}, // Format: { 'section_chipId': true/false }
  excludedChipData: {}, // Format: { 'section_chipId': { id, name, label, ... } }
};

export const useExcludedChipsStore = create(
  devtools(
    (set, get) => ({
      ...initialState,

      // Set excluded state for a chip with optional data
      setChipExcluded: (chipKey, isExcluded, chipData = null) => {
        set((state) => {
          const newState = {
            excludedChips: {
              ...state.excludedChips,
              [chipKey]: isExcluded
            },
            excludedChipData: { ...state.excludedChipData }
          };
          
          if (isExcluded && chipData) {
            newState.excludedChipData[chipKey] = chipData;
          } else if (!isExcluded) {
            delete newState.excludedChipData[chipKey];
          }
          
          return newState;
        });
      },

      // Toggle excluded state for a chip
      toggleChipExcluded: (chipKey) => {
        set((state) => ({
          excludedChips: {
            ...state.excludedChips,
            [chipKey]: !state.excludedChips[chipKey]
          }
        }));
      },

      // Get excluded state for a chip
      isChipExcluded: (chipKey) => {
        return get().excludedChips[chipKey] || false;
      },

      // Get all excluded chips for a specific section
      getExcludedChipsForSection: (section) => {
        const excluded = [];
        const chips = get().excludedChips;
        Object.keys(chips).forEach(key => {
          if (key.startsWith(`${section}_`) && chips[key]) {
            excluded.push(key);
          }
        });
        return excluded;
      },

      // Clear all excluded chips
      clearAllExcludedChips: () => {
        set({ excludedChips: {}, excludedChipData: {} });
      },

      // Clear excluded chips for a specific section
      clearExcludedChipsForSection: (section) => {
        set((state) => {
          const newExcludedChips = { ...state.excludedChips };
          const newExcludedChipData = { ...state.excludedChipData };
          Object.keys(newExcludedChips).forEach(key => {
            if (key.startsWith(`${section}_`)) {
              delete newExcludedChips[key];
              delete newExcludedChipData[key];
            }
          });
          return { excludedChips: newExcludedChips, excludedChipData: newExcludedChipData };
        });
      },

      // Get all excluded chips grouped by section
      getExcludedChipsBySection: () => {
        const chips = get().excludedChips;
        const grouped = {};
        
        Object.entries(chips).forEach(([key, isExcluded]) => {
          if (isExcluded) {
            const [section, ...idParts] = key.split('_');
            const id = idParts.join('_');
            if (!grouped[section]) {
              grouped[section] = [];
            }
            grouped[section].push(id);
          }
        });
        
        return grouped;
      },
      
      // Get chip data for a specific key
      getChipData: (chipKey) => {
        return get().excludedChipData[chipKey] || null;
      },
      
      // Get all excluded chip data grouped by section
      getExcludedChipDataBySection: () => {
        const chips = get().excludedChips;
        const chipData = get().excludedChipData;
        const grouped = {};
        
        Object.entries(chips).forEach(([key, isExcluded]) => {
          if (isExcluded && chipData[key]) {
            const [section] = key.split('_');
            if (!grouped[section]) {
              grouped[section] = [];
            }
            grouped[section].push(chipData[key]);
          }
        });
        
        return grouped;
      }
    }),
    {
      name: 'excluded-chips-store'
    }
  )
);